import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import type { CustomColumnNamesConfig, DailyPrintoutGrouping, PrintoutColumn, PrintoutColumnType } from "../types";
import { compositeGroupingKeyToDisplayName, resolveColumnHeaderText } from "../util/groupingDisplay";
import { COLUMN_HEADER_FILL, SECTION_HEADER_FILL, THIN_BORDER } from "./cellStyle";
import { emitBlankHeaderCells } from "./emptyColumns";
import { mergeAcrossRow } from "./rowMerge";

interface RenderPivotSectionHeaderArgs {
	worksheet: Worksheet;
	groupingKey: string;
	currentRow: number;
	grouping: DailyPrintoutGrouping;
	anchorColumns: PrintoutColumn[];
	subColumns: PrintoutColumn[];
	horizontalValues: string[];
	horizontalValueLabels: Map<string, string>;
	department: Department;
	jobManagementEnabled: boolean;
	columnGroupingBy: PrintoutColumnType | null;
	/** When columns are grouped by location, each band label gets ` - <census>` appended. */
	censusByLocationName: Map<string, number>;
	/** Headings for custom Start Time group keys; null when custom shift time groups are off. */
	customTimeGroupLabels: ReadonlyMap<string, string> | null;
	customColumnNames: CustomColumnNamesConfig;
	/** Leave out row 3 (the sub-column names). The band labels in row 2 stay — they are data. */
	hideColumnNames: boolean;
	/** Blank columns after the last band; the title spans them and rows 2–3 give them header cells. */
	appendEmptyColumns: number;
}

const HORIZONTAL_GROUP_FILL = { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FFB8CCE4" } };

type PivotHeaderRowArgs = Omit<RenderPivotSectionHeaderArgs, "groupingKey" | "grouping" | "department" | "hideColumnNames" | "customTimeGroupLabels">;

/** Band-labels row: anchor area (light grey, merged) + each horizontal value (blue, spanning its sub-columns). */
const emitBandLabelsRow = (args: PivotHeaderRowArgs): void => {
	const { worksheet, currentRow, anchorColumns, subColumns, horizontalValues, horizontalValueLabels, columnGroupingBy, censusByLocationName } = args;
	let colOffset = 1;
	if (anchorColumns.length > 0) {
		const anchorCell = worksheet.getCell(currentRow, colOffset);
		anchorCell.fill = COLUMN_HEADER_FILL;
		if (anchorColumns.length > 1) {
			worksheet.mergeCells(currentRow, colOffset, currentRow, colOffset + anchorColumns.length - 1);
		}
		colOffset += anchorColumns.length;
	}
	for (const hVal of horizontalValues) {
		const cell = worksheet.getCell(currentRow, colOffset);
		const label = horizontalValueLabels.get(hVal);
		if (label === undefined) throw new Error(`Missing horizontal pivot label for ${hVal}`);
		const census = columnGroupingBy === "location" ? censusByLocationName.get(label) : undefined;
		cell.value = census !== undefined ? `${label} - ${census}` : label;
		cell.font = { bold: true };
		cell.fill = HORIZONTAL_GROUP_FILL;
		cell.alignment = { horizontal: "left" };
		cell.border = THIN_BORDER;
		if (subColumns.length > 1) {
			worksheet.mergeCells(currentRow, colOffset, currentRow, colOffset + subColumns.length - 1);
		}
		colOffset += subColumns.length;
	}
	emitBlankHeaderCells({ worksheet, row: currentRow, firstColumn: colOffset, count: args.appendEmptyColumns });
};

/** Sub-column names row: anchor column names + sub-column names repeated under each band. */
const emitSubColumnNamesRow = (args: PivotHeaderRowArgs): void => {
	const { worksheet, currentRow, anchorColumns, subColumns, horizontalValues, jobManagementEnabled, customColumnNames } = args;
	let colOffset = 1;
	for (const col of anchorColumns) {
		const cell = worksheet.getCell(currentRow, colOffset);
		cell.value = resolveColumnHeaderText(col.type, jobManagementEnabled, customColumnNames);
		cell.font = { bold: true };
		cell.fill = COLUMN_HEADER_FILL;
		cell.border = THIN_BORDER;
		colOffset += 1;
	}
	for (let hIdx = 0; hIdx < horizontalValues.length; hIdx++) {
		const isLastBand = hIdx === horizontalValues.length - 1;
		for (let subIdx = 0; subIdx < subColumns.length; subIdx++) {
			const col = subColumns[subIdx];
			const cell = worksheet.getCell(currentRow, colOffset);
			cell.value = resolveColumnHeaderText(col.type, jobManagementEnabled, customColumnNames);
			cell.font = { bold: true };
			cell.fill = COLUMN_HEADER_FILL;
			const isLastInBand = subIdx === subColumns.length - 1;
			cell.border = {
				top: { style: "thin" },
				left: { style: "thin" },
				bottom: { style: "thin" },
				right: { style: isLastInBand && !isLastBand ? "medium" : "thin" },
			};
			colOffset += 1;
		}
	}
	emitBlankHeaderCells({ worksheet, row: currentRow, firstColumn: colOffset, count: args.appendEmptyColumns });
};

/**
 * Emit the section header for horizontal-pivot mode (mapping §8.2):
 * 1. Title row — composite section label, merged across all columns (appended blank ones included)
 * 2. Band-labels row — anchor area (light grey) + each horizontal value (blue, spanning its sub-columns)
 * 3. Sub-column names row — anchor column names + sub-column names repeated under each band. Left out
 *    under `hideColumnNames`, so the data starts right under the band labels.
 *
 * Returns the row number where data should begin.
 */
export const renderPivotSectionHeader = (args: RenderPivotSectionHeaderArgs): number => {
	const {
		worksheet,
		groupingKey,
		currentRow: initialRow,
		grouping,
		anchorColumns,
		subColumns,
		horizontalValues,
		department,
		jobManagementEnabled,
		censusByLocationName,
		customTimeGroupLabels,
		hideColumnNames,
		appendEmptyColumns,
	} = args;

	let currentRow = initialRow;
	const totalColumns = anchorColumns.length + subColumns.length * horizontalValues.length + appendEmptyColumns;

	// Title row
	const titleCell = worksheet.getCell(currentRow, 1);
	titleCell.value = compositeGroupingKeyToDisplayName(groupingKey, grouping, jobManagementEnabled, department, censusByLocationName, customTimeGroupLabels);
	titleCell.font = { bold: true, size: 14, color: { argb: "FF000000" } };
	titleCell.fill = SECTION_HEADER_FILL;
	titleCell.alignment = { horizontal: "left" };
	mergeAcrossRow(worksheet, currentRow, totalColumns);
	currentRow += 1;

	emitBandLabelsRow({ ...args, currentRow });
	currentRow += 1;

	if (hideColumnNames) return currentRow;
	emitSubColumnNamesRow({ ...args, currentRow });
	return currentRow + 1;
};
